import React, { useEffect, useMemo, useRef, useState } from 'react';
import RideAppIcon from './RideAppIcon';
import RidePortal from './RidePortal';

const pad = (value) => String(value).padStart(2, '0');
const dayKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const timeLabel = (date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;
const VIETNAMESE_WEEKDAYS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];

const startOfDay = (value) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const monthStart = (value) => new Date(value.getFullYear(), value.getMonth(), 1);

const calendarCells = (month) => {
  const first = monthStart(month);
  const leadingEmptyCells = (first.getDay() + 6) % 7;
  const numberOfDays = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const cells = Array.from({ length: leadingEmptyCells }, () => null);
  for (let day = 1; day <= numberOfDays; day += 1) {
    cells.push(new Date(first.getFullYear(), first.getMonth(), day));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const roundedNow = (now = new Date()) => {
  const rounded = new Date(now);
  const hasPartialMinute = rounded.getSeconds() !== 0 || rounded.getMilliseconds() !== 0;
  const remainder = rounded.getMinutes() % 5;
  const minutesToAdd = remainder === 0
    ? (hasPartialMinute ? 5 : 0)
    : 5 - remainder;
  rounded.setSeconds(0, 0);
  rounded.setMinutes(rounded.getMinutes() + minutesToAdd);
  return rounded;
};

export const rideDefaultPickupDateTime = (now = new Date()) => (
  roundedNow(new Date(now.getTime() + 2 * 60 * 60 * 1000))
);

export const RideScheduleSheet = ({ initialValue, onClose, onConfirm }) => {
  const [selected, setSelected] = useState(() => initialValue ? new Date(initialValue) : rideDefaultPickupDateTime());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => monthStart(initialValue ? new Date(initialValue) : rideDefaultPickupDateTime()));
  const selectedSlotRef = useRef(null);
  const minimumDay = startOfDay(new Date());
  const maximumDay = startOfDay(new Date(Date.now() + 30 * 86400000));
  const days = useMemo(() => calendarCells(calendarMonth), [calendarMonth]);
  const slots = useMemo(() => {
    const start = roundedNow();
    const day = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate());
    const first = dayKey(day) === dayKey(start) ? start : day;
    return Array.from({ length: 288 }, (_, index) => new Date(first.getTime() + index * 5 * 60 * 1000))
      .filter((slot) => dayKey(slot) === dayKey(day));
  }, [selected]);

  useEffect(() => {
    selectedSlotRef.current?.scrollIntoView?.({ block: 'center' });
  }, [selected]);
  const chooseDay = (offset) => {
    const base = new Date();
    base.setHours(selected.getHours(), selected.getMinutes(), 0, 0);
    base.setDate(base.getDate() + offset);
    setCalendarOpen(false);
    if (base < roundedNow()) setSelected(roundedNow()); else setSelected(base);
  };
  const chooseCalendarDay = (day) => {
    const next = new Date(day.getFullYear(), day.getMonth(), day.getDate(), selected.getHours(), selected.getMinutes());
    setSelected(next < roundedNow() ? roundedNow() : next);
    setCalendarOpen(false);
  };
  const toggleCalendar = () => {
    setCalendarMonth(monthStart(selected));
    setCalendarOpen((open) => !open);
  };
  const previousMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1);
  const nextMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1);
  const previousMonthDisabled = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 0) < minimumDay;
  const nextMonthDisabled = nextMonth > maximumDay;

  return (
    <RidePortal>
      <div className="ride-option-backdrop" role="presentation" onMouseDown={onClose}>
        <section className="ride-option-sheet ride-schedule-sheet" role="dialog" aria-modal="true" aria-label="Bạn muốn xe đón lúc nào?" onMouseDown={(event) => event.stopPropagation()}>
          <div className="ride-option-sheet__head"><div><h3>Bạn muốn xe đón lúc nào?</h3><p>Chọn thời điểm đón. Bạn có thể hủy trước khi tài xế đến.</p></div><button type="button" aria-label="Đóng" onClick={onClose}><RideAppIcon name="close" /></button></div>
          <div className="ride-day-chips">
            <button type="button" className={dayKey(selected) === dayKey(new Date()) ? 'active' : ''} onClick={() => chooseDay(0)}>Hôm nay</button>
            <button type="button" className={dayKey(selected) === dayKey(new Date(Date.now() + 86400000)) ? 'active' : ''} onClick={() => chooseDay(1)}>Ngày mai</button>
            <button type="button" className={calendarOpen ? 'active' : ''} aria-expanded={calendarOpen} onClick={toggleCalendar}><RideAppIcon name="calendar" /> Ngày khác</button>
          </div>
          {calendarOpen && <div className="ride-vietnamese-calendar" aria-label="Lịch chọn ngày">
            <div className="ride-vietnamese-calendar__header">
              <button type="button" aria-label="Tháng trước" disabled={previousMonthDisabled} onClick={() => setCalendarMonth(previousMonth)}>‹</button>
              <strong>Tháng {calendarMonth.getMonth() + 1} năm {calendarMonth.getFullYear()}</strong>
              <button type="button" aria-label="Tháng sau" disabled={nextMonthDisabled} onClick={() => setCalendarMonth(nextMonth)}>›</button>
            </div>
            <div className="ride-vietnamese-calendar__weekdays">
              {VIETNAMESE_WEEKDAYS.map((weekday) => <span key={weekday}>{weekday}</span>)}
            </div>
            <div className="ride-vietnamese-calendar__days">
              {days.map((day, index) => {
                if (!day) return <span key={`empty-${index}`} aria-hidden="true" />;
                const disabled = day < minimumDay || day > maximumDay;
                const selectedDay = dayKey(day) === dayKey(selected);
                const today = dayKey(day) === dayKey(new Date());
                return <button
                  type="button"
                  key={dayKey(day)}
                  className={`${selectedDay ? 'selected' : ''} ${today ? 'today' : ''}`.trim()}
                  disabled={disabled}
                  aria-label={`Chọn ngày ${pad(day.getDate())}/${pad(day.getMonth() + 1)}/${day.getFullYear()}`}
                  aria-pressed={selectedDay}
                  onClick={() => chooseCalendarDay(day)}
                >{day.getDate()}</button>;
              })}
            </div>
            <small>Chọn ngày trong 30 ngày tới</small>
          </div>}
          <div className="ride-time-slots" aria-label="Chọn giờ đón">
            {slots.map((slot) => {
              const isSelected = timeLabel(slot) === timeLabel(selected);
              return <button ref={isSelected ? selectedSlotRef : null} type="button" key={slot.toISOString()} className={isSelected ? 'active' : ''} onClick={() => setSelected(slot)}><span>{dayKey(slot) === dayKey(new Date()) ? 'Hôm nay' : dayKey(slot) === dayKey(new Date(Date.now() + 86400000)) ? 'Ngày mai' : `${pad(slot.getDate())}/${pad(slot.getMonth() + 1)}`}</span><b>{timeLabel(slot)}</b></button>;
            })}
          </div>
          <div className="ride-option-sheet__summary">Xe đón bạn lúc <b>{timeLabel(selected)}</b> · {pad(selected.getDate())}/{pad(selected.getMonth() + 1)}/{selected.getFullYear()}</div>
          <button type="button" className="ride-option-sheet__confirm" onClick={() => onConfirm(selected.toISOString())}>Xác nhận</button>
        </section>
      </div>
    </RidePortal>
  );
};

export const RideSeatSheet = ({ initialSeats, onClose, onConfirm }) => {
  const [seats, setSeats] = useState(initialSeats);
  return (
    <RidePortal>
      <div className="ride-option-backdrop" role="presentation" onMouseDown={onClose}>
        <section className="ride-option-sheet ride-seat-sheet" role="dialog" aria-modal="true" aria-label="Chọn số ghế" onMouseDown={(event) => event.stopPropagation()}>
          <div className="ride-option-sheet__head"><div><h3>Chọn số ghế</h3><p>Số ghế cần đặt cho chuyến xe ghép.</p></div><button type="button" aria-label="Đóng" onClick={onClose}><RideAppIcon name="close" /></button></div>
          <p className="ride-seat-sheet__label">Số ghế cần đặt</p>
          <div className="ride-seat-stepper"><button type="button" aria-label="Giảm số ghế" disabled={seats <= 1} onClick={() => setSeats(seats - 1)}><RideAppIcon name="minus" /></button><strong>{seats}</strong><button type="button" aria-label="Tăng số ghế" disabled={seats >= 9} onClick={() => setSeats(seats + 1)}><RideAppIcon name="add" /></button></div>
          <button type="button" className="ride-option-sheet__confirm" onClick={() => onConfirm(seats)}>Xong</button>
        </section>
      </div>
    </RidePortal>
  );
};
